import axios from 'axios';

const cache = new Map<string, string>();
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAP_API_KEY;

// Track the total number of characters translated
const translatedCharCount = ref(0);
const translationLanguage = ref('en');
const isTranslatedView = ref(false);
const wasLastTranslationFromApi = ref(false);

function resetTranslatedCharCount() {
	translatedCharCount.value = 0;
}

function setTranslatedView(val: boolean) {
	isTranslatedView.value = val;
}

function decodeHtmlEntities(str: string): string {
	const txt = document.createElement('textarea');
	txt.innerHTML = str;
	return txt.value;
}

async function _translateObject<T extends Record<string, string>>(
	obj: T,
	targetLang: string,
): Promise<{ result: T; error: Error | null }> {
	const keys = Object.keys(obj) as Array<keyof T>;
	const contents: string[] = [];
	const keyMap: (keyof T)[] = [];

	keys.forEach((k) => {
		const text = obj[k] ?? '';
		const cacheKey = `${text}__${targetLang}`;
		if (!cache.has(cacheKey)) {
			contents.push(text);
			keyMap.push(k);
		}
	});

	if (contents.length > 0) {
		try {
			const res = await axios.post(
				`https://translation.googleapis.com/language/translate/v2?key=${GOOGLE_API_KEY}`,
				{ q: contents, target: targetLang },
			);
			const translatedBatch = res.data.data.translations.map((t: any) => t.translatedText);
			translatedBatch.forEach((tr: string, idx: number) => {
				const k = keyMap[idx];
				const original = obj[k];
				const decoded = decodeHtmlEntities(tr);
				cache.set(`${original}__${targetLang}`, decoded);
			});
			wasLastTranslationFromApi.value = true;
		} catch (err: any) {
			console.error('Translation API error:', err);
			// On error, we'll just return the original text for any failed translations
			const result = { ...obj };
			return { result, error: err };
		}
	} else {
		wasLastTranslationFromApi.value = false;
	}

	const result = {} as T;
	keys.forEach((k) => {
		const text = obj[k];
		const cacheKey = `${text}__${targetLang}`;
		if (cache.has(cacheKey)) {
			result[k] = decodeHtmlEntities(cache.get(cacheKey)!) as T[keyof T];
		} else {
			result[k] = text as T[keyof T];
		}
	});
	return { result, error: null };
}

export function useTranslation() {
	const isTranslating = ref(false);
	const error = ref<Error | null>(null);

	async function translate<T extends Record<string, string>>(
		obj: T,
		eventCountryCode?: number,
	): Promise<T> {
		if (typeof eventCountryCode !== 'undefined' && eventCountryCode === COUNTRY_CODE.UNITED_STATES) {
			return obj;
		}

		const browserLang = getBrowserLanguage();
		translationLanguage.value = browserLang;
		isTranslating.value = true;
		error.value = null;

		// Count characters to be translated (sum of all string values in obj)
		const charCount = Object.values(obj).reduce((acc, val) => acc + (val?.length || 0), 0);

		try {
			const { result: translatedResult, error: translationError } = await _translateObject(obj, browserLang);
			if (translationError) {
				error.value = translationError;
			} else {
				// Count characters translated
				translatedCharCount.value += charCount;
			}
			return translatedResult;
		} catch (err: any) {
			error.value = err;
			return obj;
		} finally {
			isTranslating.value = false;
		}
	}

	return {
		isTranslatedView,
		isTranslating,
		error,
		translate,
		setTranslatedView,
		translatedCharCount,
		resetTranslatedCharCount,
		translationLanguage,
		wasLastTranslationFromApi,
	};
}